import { create } from 'zustand';
import type { ConnectionConfig, TunnelConfig } from '@/types';

/**
 * Shared modal state for connection + tunnel editors.
 *
 * Previously lived as local state in AppShell, which meant only the sidebar
 * (which gets callbacks as props) could trigger the modal. Lifting it into a
 * store lets any component (InfoView's Actions panel, mobile screens, the
 * command palette, etc.) open the same modal via `openEditConnection(conn)`
 * / `openNewConnection()`, and the singleton modal lives at the app root.
 */
export type ConnectionModalState =
  | { mode: 'closed' }
  | { mode: 'new' }
  | { mode: 'edit'; connection: ConnectionConfig };

export type TunnelModalState =
  | { mode: 'closed' }
  | { mode: 'new' }
  | { mode: 'edit'; tunnel: TunnelConfig };

export type DatabaseModalState =
  | { mode: 'closed' }
  | { mode: 'create'; connectionId: string }
  | { mode: 'edit'; connectionId: string; name: string };

/** Settings modal can open on a specific tab/field so `> font size` in the
 *  command bar jumps the user straight to the relevant control. `focus` is
 *  the setting key (e.g., `editor.fontSize`); the modal scrolls to it. */
export type SettingsModalState =
  | { mode: 'closed' }
  | { mode: 'open'; focus?: string };

/** Share dialog can be opened against either a connection or a tunnel. */
export type ShareDialogState =
  | { mode: 'closed' }
  | { mode: 'open'; resourceType: 'connection' | 'tunnel'; resourceId: string; resourceName: string };

interface ModalStore {
  connectionModal: ConnectionModalState;
  tunnelModal: TunnelModalState;
  databaseModal: DatabaseModalState;
  settingsModal: SettingsModalState;
  shareDialog: ShareDialogState;

  openNewConnection: () => void;
  openEditConnection: (connection: ConnectionConfig) => void;
  closeConnectionModal: () => void;

  openNewTunnel: () => void;
  openEditTunnel: (tunnel: TunnelConfig) => void;
  closeTunnelModal: () => void;

  openCreateDatabase: (connectionId: string) => void;
  openEditDatabase: (connectionId: string, name: string) => void;
  closeDatabaseModal: () => void;

  openSettings: (focus?: string) => void;
  closeSettings: () => void;

  openShareDialog: (params: { resourceType: 'connection' | 'tunnel'; resourceId: string; resourceName: string }) => void;
  closeShareDialog: () => void;
}

export const useModalStore = create<ModalStore>((set) => ({
  connectionModal: { mode: 'closed' },
  tunnelModal: { mode: 'closed' },
  databaseModal: { mode: 'closed' },
  settingsModal: { mode: 'closed' },
  shareDialog: { mode: 'closed' },

  openNewConnection: () => set({ connectionModal: { mode: 'new' } }),
  openEditConnection: (connection) => set({ connectionModal: { mode: 'edit', connection } }),
  closeConnectionModal: () => set({ connectionModal: { mode: 'closed' } }),

  openNewTunnel: () => set({ tunnelModal: { mode: 'new' } }),
  openEditTunnel: (tunnel) => set({ tunnelModal: { mode: 'edit', tunnel } }),
  closeTunnelModal: () => set({ tunnelModal: { mode: 'closed' } }),

  openCreateDatabase: (connectionId) =>
    set({ databaseModal: { mode: 'create', connectionId } }),
  openEditDatabase: (connectionId, name) =>
    set({ databaseModal: { mode: 'edit', connectionId, name } }),
  closeDatabaseModal: () => set({ databaseModal: { mode: 'closed' } }),

  openSettings: (focus) => set({ settingsModal: { mode: 'open', focus } }),
  closeSettings: () => set({ settingsModal: { mode: 'closed' } }),

  openShareDialog: ({ resourceType, resourceId, resourceName }) =>
    set({ shareDialog: { mode: 'open', resourceType, resourceId, resourceName } }),
  closeShareDialog: () => set({ shareDialog: { mode: 'closed' } }),
}));
